import type { WebGLRenderer } from "three";

interface GpuTimerExt {
  readonly TIME_ELAPSED_EXT: number;
  readonly GPU_DISJOINT_EXT: number;
}

/**
 * One TIME_ELAPSED measurement channel. Only one TIME_ELAPSED query may be
 * active in a context at a time, so channels must bracket disjoint (non-nested)
 * GPU spans — here the sim pass ends before the render pass begins. Each channel
 * keeps a single in-flight query and polls it the next frame; the disjoint flag
 * is read once per frame by StatsUI and passed in so the two channels agree on
 * one reading (querying GPU_DISJOINT_EXT clears it).
 */
class GpuTimerChannel {
  private pendingQuery: WebGLQuery | null = null;
  private active = false;
  private lastMs = 0;

  constructor(private gl: WebGL2RenderingContext, private ext: GpuTimerExt) {}

  poll(disjoint: boolean): void {
    if (!this.pendingQuery) return;
    const available = this.gl.getQueryParameter(this.pendingQuery, this.gl.QUERY_RESULT_AVAILABLE) as boolean;
    if (available && !disjoint) {
      const ns = this.gl.getQueryParameter(this.pendingQuery, this.gl.QUERY_RESULT) as number;
      this.lastMs = ns / 1e6;
    }
    if (available || disjoint) {
      this.gl.deleteQuery(this.pendingQuery);
      this.pendingQuery = null;
    }
  }

  begin(): void {
    if (this.pendingQuery) return;
    const q = this.gl.createQuery();
    if (!q) return;
    this.pendingQuery = q;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.active = true;
  }

  end(): void {
    if (!this.active) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.active = false;
  }

  get ms(): number {
    return this.lastMs;
  }
}

export interface FrameSample {
  t: number;
  gpuRenderMs: number;
  gpuSimMs: number;
  readbackMs: number;
  downstreamMs: number;
  cpuMs: number;
  drawCalls: number;
  triangles: number;
  substeps: number;
  contactsNonZero: number;
  outlineActive: boolean;
  disjoint: boolean;
}

const RING_CAPACITY = 120;
const WINDOW_MS = 1000;

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor(p * (sorted.length - 1)));
  return sorted[idx];
}

export class StatsUI {
  private el: HTMLDivElement;
  private textEl: HTMLDivElement;
  private detailEl: HTMLDivElement;
  private lastDisplayMs = 0;
  private gl: WebGL2RenderingContext;
  private ext: GpuTimerExt | null;
  private renderChannel: GpuTimerChannel | null;
  private simChannel: GpuTimerChannel | null;
  private phaseChannels: Map<string, GpuTimerChannel> = new Map();
  private ring: FrameSample[] = [];
  private ringHead = 0;
  private debug: boolean;

  constructor(renderer: WebGLRenderer) {
    this.debug = new URLSearchParams(location.search).has("debug");
    this.el = document.createElement("div");
    this.el.className = "stats";
    this.textEl = document.createElement("div");
    this.detailEl = document.createElement("div");
    this.detailEl.className = "stats-detail";
    this.el.append(this.textEl, this.detailEl);
    document.body.appendChild(this.el);
    this.gl = renderer.getContext() as WebGL2RenderingContext;
    this.ext = this.gl.getExtension("EXT_disjoint_timer_query_webgl2") as GpuTimerExt | null;
    this.renderChannel = this.ext ? new GpuTimerChannel(this.gl, this.ext) : null;
    this.simChannel = this.ext ? new GpuTimerChannel(this.gl, this.ext) : null;
  }

  /** Reads the disjoint flag once and polls every channel's in-flight query. */
  pollFrame(): boolean {
    if (!this.ext) return false;
    const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT) as boolean;
    this.renderChannel?.poll(disjoint);
    this.simChannel?.poll(disjoint);
    for (const channel of this.phaseChannels.values()) {
      channel.poll(disjoint);
    }
    return disjoint;
  }

  /**
   * Per-render-pass timing for the bench breakdown. Each named phase gets its
   * own channel (created lazily on first begin); callers must bracket disjoint,
   * non-nested GPU spans — the composer passes run sequentially and the outer
   * render channel is left off during bench so only one TIME_ELAPSED query is
   * ever active. Returns null when the timer extension is absent.
   */
  beginPhase(name: string): void {
    if (!this.ext) return;
    let channel = this.phaseChannels.get(name);
    if (!channel) {
      channel = new GpuTimerChannel(this.gl, this.ext);
      this.phaseChannels.set(name, channel);
    }
    channel.begin();
  }

  endPhase(name: string): void {
    this.phaseChannels.get(name)?.end();
  }

  phaseMs(name: string): number | null {
    if (!this.ext) return null;
    return this.phaseChannels.get(name)?.ms ?? null;
  }

  beginSim(): void {
    this.simChannel?.begin();
  }

  endSim(): void {
    this.simChannel?.end();
  }

  beginRender(): void {
    this.renderChannel?.begin();
  }

  endRender(): void {
    this.renderChannel?.end();
  }

  get gpuTimingAvailable(): boolean {
    return this.ext !== null;
  }

  get gpuRenderMs(): number {
    return this.renderChannel?.ms ?? 0;
  }

  get gpuSimMs(): number {
    return this.simChannel?.ms ?? 0;
  }

  recordFrame(sample: FrameSample): void {
    if (this.ring.length < RING_CAPACITY) {
      this.ring.push(sample);
    } else {
      this.ring[this.ringHead] = sample;
      this.ringHead = (this.ringHead + 1) % RING_CAPACITY;
    }
  }

  setVisible(visible: boolean): void {
    this.el.style.display = visible ? "" : "none";
  }

  private detailLines(now: number): string[] {
    const win = this.ring.filter((s: FrameSample): boolean => now - s.t <= WINDOW_MS);
    if (win.length === 0) return [];
    const renderSorted = win.map((s: FrameSample): number => s.gpuRenderMs).sort((a: number, b: number): number => a - b);
    const simSorted = win.map((s: FrameSample): number => s.gpuSimMs).sort((a: number, b: number): number => a - b);
    const rbSorted = win.map((s: FrameSample): number => s.readbackMs).sort((a: number, b: number): number => a - b);
    const dsSorted = win.map((s: FrameSample): number => s.downstreamMs).sort((a: number, b: number): number => a - b);
    const disjointN = win.filter((s: FrameSample): boolean => s.disjoint).length;
    const fmtP = (arr: number[]): string => `${percentile(arr, 0.5).toFixed(2)}/${percentile(arr, 0.95).toFixed(2)}/${percentile(arr, 0.99).toFixed(2)}/${arr[arr.length - 1].toFixed(2)}`;
    const lines = [
      `Rndr p50/95/99/max ${fmtP(renderSorted)}ms`,
      `Sim  p50/95/99/max ${fmtP(simSorted)}ms`,
      `RdBk p50/95/99/max ${fmtP(rbSorted)}ms`,
      `Down p50/95/99/max ${fmtP(dsSorted)}ms`,
      `disjoint ${disjointN}/${win.length}`,
    ];
    return lines;
  }

  update(
    now: number,
    fps: number,
    cpuMs: number,
    drawCalls: number,
    triangles: number,
    vramMb: string,
  ): void {
    if (now - this.lastDisplayMs < 250) return;
    this.lastDisplayMs = now;
    const gpuStr = this.ext ? `${this.gpuRenderMs.toFixed(1)}ms` : "—";
    this.textEl.textContent = [
      `build ${__COMMIT_ID__}`,
      `FPS ${Math.round(fps)}`,
      `Draw ${drawCalls}`,
      `Tri ${(triangles / 1000).toFixed(1)}k`,
      `CPU ${cpuMs.toFixed(1)}ms`,
      `GPU ${gpuStr}`,
      `VRAM ${vramMb}`,
    ].join("\n");
    this.detailEl.textContent = this.debug && this.ext ? this.detailLines(now).join("\n") : "";
  }
}
